import constant from 'lodash/constant'

import { FILING_IN_PROGRESS_MESSAGE } from '@klear/constants/document-processing'
import { useToast } from '@klear/ui/hooks/use-toast'
import { trpc } from '@klear/ui/lib/trpc'

export function useDocumentReviewActions() {
  const utils = trpc.useUtils()
  const { toast } = useToast()

  const invalidate = () => {
    void utils.documentReview.invalidate()
    void utils.documents.invalidate()
  }

  const approve = trpc.documentReview.approve.useMutation({
    onSuccess: (detail) => {
      invalidate()
      toast({
        title: 'Records created',
        description: `The records exist and the document is filed under ${detail.traderName ?? 'its trader'}.`
      })
    },
    onError: async (error, { processingId }) => {
      invalidate()
      if (error.message === FILING_IN_PROGRESS_MESSAGE) {
        toast({ title: 'Another filing is in progress', description: error.message })
        return
      }
      const review =
        error.data?.code === 'CONFLICT'
          ? await utils.documentReview.get.fetch({ processingId }).catch(constant(null))
          : null
      if (review != null && review.status !== 'failed') {
        toast({ title: 'This run is out of date', description: error.message })
        return
      }
      toast({ title: 'Nothing was created', description: error.message, variant: 'destructive' })
    }
  })

  const deny = trpc.documentReview.deny.useMutation({
    onSuccess: () => {
      invalidate()
      toast({ title: 'Dismissed', description: 'Nothing was created, and nothing was deleted.' })
    },
    onError: (error) => {
      toast({ title: 'Dismiss did not go through', description: error.message, variant: 'destructive' })
    }
  })

  const update = trpc.documentReview.update.useMutation({ onSuccess: invalidate })

  const process = trpc.documentReview.process.useMutation({
    onSuccess: (result, variables) => {
      invalidate()
      if (result.outcome === 'queued') {
        if (variables.documentId == null) toast({ title: 'Processing it again' })
        return
      }
      if (result.outcome === 'deduped') {
        toast({ title: 'Already processing' })
        return
      }
      toast({
        title: 'Document processing is switched off here',
        description: 'Nothing will run in this environment until it is switched on.',
        variant: 'destructive'
      })
    },
    onError: (error) => {
      toast({ title: "Couldn't process the document", description: error.message, variant: 'destructive' })
    }
  })

  const fileAsUpload = trpc.documentReview.fileAsUpload.useMutation({
    onSuccess: (detail) => {
      invalidate()
      toast({
        title: 'Filed as an upload',
        description: `On ${detail.traderName ?? 'the trader'}’s Documents tab.`
      })
    },
    onError: (error) => {
      invalidate()
      toast({ title: 'Nothing was filed', description: error.message, variant: 'destructive' })
    }
  })

  return { approve, deny, update, process, fileAsUpload }
}
